import React, {useEffect, useState} from "react";
import Loading from "components/Loading";
import Header from "components/Header";
import {DESCRIPTION_CLASS, TEXT_INPUT_THEME, TITLE_CLASS} from "components/Survey/classes";
import {Button, TextInput} from "flowbite-react";
import {Link, useSearchParams} from "react-router-dom";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import {httpsCallable} from "firebase/functions";
import {functions} from "hooks/firebase";
import {SendTransactionalEmailRequest} from "data/models/shared/send-transactional-email";


const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [finished, setFinished] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(window.localStorage.getItem('emailForSignIn'))
  const [formData, setFormData] = useState({
    email: '',
  });

  const from = searchParams.get("from");

  useEffect(() => {
    if (userEmail === null) {
      return;
    }

    setLoading(true);
    const fromParam = from ? `?from=${from}` : "";

    const sendTransactionalEmailFunction = httpsCallable<SendTransactionalEmailRequest, void>(functions, 'sendTransactionalEmail');

    sendTransactionalEmailFunction({
      emailKind: "sign-in-link",
      recipient: {
        email: userEmail,
        name: null,
      },
      data: {
        Company: null,
        fromParam,
        appUrl: window.location.origin,
        linkDomain: window.location.host,
      }
    }).then(() => {
      // The link was successfully sent. Inform the user.
      // Save the email locally so you don't need to ask the user for it again
      // if they open the link on the same device.
      setLoading(false);
      setFinished(true);
      window.localStorage.setItem('emailForSignIn', userEmail);
      // ...
    }).catch((error) => {
      setFinished(true);
      Sentry.captureException(error);
    });
  }, [userEmail]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const {id, value} = event.target;
    setFormData({
      ...formData,
      [id]: value,
    });
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    setUserEmail(formData.email);
  }

  function login() {
    window.localStorage.removeItem("emailForSignIn");
    window.location.reload();
  }

  if (finished && loading) {
    return <div>
      An error happened while attempting to send you an email.
    </div>
  }

  if (loading) {
    return <Loading className={"mt-24"} loadingText={"Logging you in .."}/>;
  }

  return <>
    <Header saveFormCallback={null} hideSignIn={true} />
    <div className={"flex flex-col"}>
      {finished && <div className={"mt-32 flex flex-row justify-center"}>
          <div className={"flex flex-col gap-4 text-center"}>
              <div className={clsx(TITLE_CLASS, "mb-12")}>Check your email for your sign in link!</div>
              <div>
                <span className={"text-xl text-neutral-500 leading-relaxed"}>
                    An email with a login link has been sent to {userEmail}.<br/>
                    Click the link to sign in.
                </span>
              </div>
              <div>
                <span className={"text-xl text-neutral-500 leading-relaxed"}>
                    Need to use another email address or send another login link, </span>
                  <Link
                      color={"transparent"}
                      to={"#"}
                      onClick={() => login()}
                      className="p-0"
                  ><span className={"underline text-xl text-neutral-500"}>click here.</span></Link>

              </div>
          </div>
      </div>}
      {!finished && <div className="flex flex-col">
          <div className={"mt-32 flex flex-row justify-center"}>
              <span className={TITLE_CLASS}>Sign In</span>
          </div>
          <form onSubmit={handleSubmit}>
              <div className={"flex flex-col max-w-screen-md mx-auto items-center"}>
                  <div className="mt-8 w-full text-center">
                      <label
                          htmlFor="email"
                          className={DESCRIPTION_CLASS}
                      >Enter your email address and we will send you a link.</label>
                      <TextInput
                          className={"mt-8"}
                          theme={TEXT_INPUT_THEME}
                          sizing={"lg"}
                          type="email" id="email"
                          placeholder="john.doe@company.com"
                          required
                          value={formData.email} onChange={handleChange}
                      />
                  </div>
                  <div className={"mt-12"}>
                      <Button
                          className={"min-w-24 ml-1 rounded-full"}
                          color={"blue"}
                          type={"submit"}
                      >Sign In</Button>
                  </div>
              </div>
          </form>
      </div>}
    </div>
  </>
}

export default LoginPage;
